import { useEffect, useState, useRef } from 'react';
import api, { BACKEND_URL } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { Star, Car, Camera, X, Warning, Trash, MapPin, Calendar, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

export function Reviews({ eventId }) {
  const { user, login } = useAuth();
  const [items, setItems] = useState([]);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');

  const load = () => api.get(`/events/${eventId}/reviews`).then(r => setItems(r.data));
  useEffect(load, [eventId]);

  const submit = async () => {
    if (!user) return login();
    if (!text.trim()) return;
    try {
      await api.post(`/events/${eventId}/reviews`, { rating, text });
      setText(''); setRating(5); load();
      toast.success('Thanks for sharing!');
    } catch { toast.error('Could not post review'); }
  };

  const avg = items.length ? (items.reduce((a,b)=>a+b.rating,0)/items.length).toFixed(1) : null;

  return (
    <section data-testid="reviews-section">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-display text-2xl">Reviews</h2>
        {avg && <span className="text-sand font-display text-lg flex items-center gap-1"><Star size={18} weight="fill" /> {avg} <span className="text-xs text-muted-foreground">({items.length})</span></span>}
      </div>
      <div className="bg-card border border-border rounded-2xl p-5 mb-4">
        <div className="flex items-center gap-1 mb-3">
          {[1,2,3,4,5].map(n => (
            <button key={n} onClick={() => setRating(n)} type="button" data-testid={`star-${n}`}>
              <Star size={22} weight={n <= rating ? 'fill' : 'regular'} className={n <= rating ? 'text-sand' : 'text-muted-foreground'} />
            </button>
          ))}
        </div>
        <textarea value={text} onChange={e=>setText(e.target.value)} rows={3} placeholder="Share your experience…" className="w-full bg-transparent border border-border rounded-xl p-3 text-sm" data-testid="review-text" />
        <Button onClick={submit} className="mt-3 bg-forest text-bone rounded-full" data-testid="review-submit">Post review</Button>
      </div>
      <div className="space-y-3">
        {items.map(r => (
          <div key={r.review_id} className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-medium text-sm">{r.user_name}</p>
              <span className="flex">{Array.from({length: r.rating}).map((_,i)=><Star key={i} size={12} weight="fill" className="text-sand" />)}</span>
            </div>
            <p className="text-sm text-muted-foreground">{r.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function RideShare({ eventId }) {
  const { user, login } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ kind: 'offer', from_city: '', seats: 1, notes: '', contact: '' });

  const load = () => api.get(`/events/${eventId}/rides`).then(r => setItems(r.data));
  useEffect(load, [eventId]);

  const post = async () => {
    if (!user) return login();
    if (!form.from_city || !form.contact) return toast.error('From city and contact required');
    await api.post(`/events/${eventId}/rides`, form);
    setForm({ kind: 'offer', from_city: '', seats: 1, notes: '', contact: '' });
    load();
  };

  return (
    <section data-testid="rideshare-section">
      <h2 className="font-display text-2xl mb-4 flex items-center gap-2"><Car size={22} weight="duotone" /> Ride share board</h2>
      <div className="bg-card border border-border rounded-2xl p-5 mb-4 grid sm:grid-cols-2 gap-3">
        <select value={form.kind} onChange={e=>setForm({...form, kind: e.target.value})} className="bg-transparent border border-border rounded-xl p-2 text-sm">
          <option value="offer">I'm offering a ride</option>
          <option value="request">I need a ride</option>
        </select>
        <input value={form.from_city} onChange={e=>setForm({...form, from_city: e.target.value})} placeholder="Departing from (city)" className="bg-transparent border border-border rounded-xl p-2 text-sm" data-testid="ride-city" />
        <input type="number" min="1" max="8" value={form.seats} onChange={e=>setForm({...form, seats: +e.target.value})} placeholder="Seats" className="bg-transparent border border-border rounded-xl p-2 text-sm" />
        <input value={form.contact} onChange={e=>setForm({...form, contact: e.target.value})} placeholder="Contact (email/phone/IG)" className="bg-transparent border border-border rounded-xl p-2 text-sm" data-testid="ride-contact" />
        <input value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} placeholder="Notes" className="bg-transparent border border-border rounded-xl p-2 text-sm sm:col-span-2" />
        <Button onClick={post} className="bg-terracotta text-white rounded-full sm:col-span-2" data-testid="ride-post">Post</Button>
      </div>
      <div className="space-y-2">
        {items.map(r => (
          <div key={r.ride_id} className="bg-card border border-border rounded-xl p-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-sm"><span className={`label ${r.kind==='offer'?'text-turquoise':'text-crimson'}`}>{r.kind==='offer'?'Offering':'Needs ride'}</span> from <strong>{r.from_city}</strong> · {r.seats} seat(s)</p>
              {r.notes && <p className="text-xs text-muted-foreground">{r.notes}</p>}
              <p className="text-xs text-muted-foreground">{r.user_name} · {r.contact}</p>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground">No ride posts yet — be the first.</p>}
      </div>
    </section>
  );
}

export function PhotoGallery({ eventId }) {
  const { user, login } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [caption, setCaption] = useState('');
  const fileRef = useRef(null);

  const load = () => api.get(`/events/${eventId}/photos`).then(r => setPhotos(r.data)).catch(() => {});
  useEffect(load, [eventId]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!user) { login(); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error('Photo must be under 10 MB'); return; }
    const fd = new FormData();
    fd.append('file', f);
    fd.append('caption', caption || '');
    try {
      setUploading(true);
      await api.post(`/events/${eventId}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo added to the gallery');
      setCaption('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const report = async (id) => {
    if (!user) return login();
    try {
      const r = await api.post(`/photos/${id}/report`);
      if (r.data.hidden) toast.success('Photo hidden — thanks for reporting');
      else if (r.data.already_reported) toast.info('You already reported this');
      else toast.success('Report submitted');
      load();
    } catch { toast.error('Could not report photo'); }
  };

  const del = async (id) => {
    if (!confirm('Delete this photo?')) return;
    try {
      await api.delete(`/photos/${id}`);
      toast.success('Deleted');
      setLightbox(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };

  return (
    <section data-testid="photo-gallery">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl flex items-center gap-2">
          <Camera size={22} weight="duotone" className="text-turquoise" /> Photo gallery
          {photos.length > 0 && <span className="text-sm text-muted-foreground font-sans">({photos.length})</span>}
        </h2>
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 mb-4">
        <p className="text-sm text-muted-foreground mb-3">Attended this gathering? Share photos with the community.</p>
        <input
          value={caption}
          onChange={e => setCaption(e.target.value.slice(0, 280))}
          placeholder="Optional caption (280 chars)"
          className="w-full bg-transparent border border-border rounded-xl p-2 text-sm mb-3"
          data-testid="photo-caption"
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif"
          onChange={onFile}
          className="hidden"
          data-testid="photo-file-input"
        />
        <Button
          onClick={() => user ? fileRef.current?.click() : login()}
          disabled={uploading}
          className="bg-turquoise text-navy hover:bg-turquoise/90 rounded-full"
          data-testid="photo-upload-btn"
        >
          <Camera size={16} className="mr-2" />
          {uploading ? 'Uploading…' : user ? 'Upload photo' : 'Sign in to upload'}
        </Button>
      </div>

      {photos.length === 0 ? (
        <p className="text-sm text-muted-foreground">No photos yet — be the first to share a memory.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {photos.map(p => (
            <button
              key={p.photo_id}
              onClick={() => setLightbox(p)}
              className="relative aspect-square overflow-hidden rounded-xl bg-muted group focus:ring-2 focus:ring-turquoise focus:outline-none"
              data-testid={`photo-thumb-${p.photo_id}`}
            >
              <img
                src={`${BACKEND_URL}${p.url}`}
                alt={p.caption || `Photo by ${p.uploaded_by_name}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {p.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/80 to-transparent p-2 text-left opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-xs text-bone line-clamp-2">{p.caption}</p>
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 bg-ink/95 z-[10000] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="photo-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 text-bone/80 hover:text-bone"
            aria-label="Close"
          >
            <X size={28} />
          </button>
          <div className="max-w-5xl w-full" onClick={e => e.stopPropagation()}>
            <img
              src={`${BACKEND_URL}${lightbox.url}`}
              alt={lightbox.caption || ''}
              className="w-full max-h-[75vh] object-contain rounded-xl"
            />
            <div className="mt-4 flex items-center justify-between text-bone">
              <div>
                <p className="text-sm">{lightbox.caption || <span className="text-bone/60 italic">No caption</span>}</p>
                <p className="text-xs text-bone/60 mt-1">By {lightbox.uploaded_by_name}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => report(lightbox.photo_id)}
                  className="text-xs text-bone/70 hover:text-crimson flex items-center gap-1"
                  data-testid="photo-report-btn"
                >
                  <Warning size={14} /> Report
                </button>
                {(user?.user_id === lightbox.uploaded_by || user?.role === 'admin') && (
                  <button
                    onClick={() => del(lightbox.photo_id)}
                    className="text-xs text-bone/70 hover:text-crimson flex items-center gap-1"
                    data-testid="photo-delete-btn"
                  >
                    <Trash size={14} /> Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


export function NearbyEvents({ eventId }) {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/events/${eventId}/nearby?limit=3`)
      .then(r => setEvents(r.data || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [eventId]);

  if (!loaded || events.length === 0) return null;

  return (
    <section data-testid="nearby-events">
      <h2 className="font-display text-2xl mb-4">More pow wows nearby</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {events.map(e => (
          <Link
            key={e.event_id}
            to={`/events/${e.event_id}`}
            className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-turquoise transition-colors"
            data-testid={`nearby-${e.event_id}`}
          >
            {e.hero_image ? (
              <div className="aspect-[16/10] overflow-hidden bg-muted">
                <img
                  src={e.hero_image}
                  alt={`${e.name} — ${e.city}, ${e.state}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            ) : (
              <div className="aspect-[16/10] bg-forest pattern-weave" />
            )}
            <div className="p-4">
              <p className="label text-terracotta mb-1">{e.tribe}</p>
              <h3 className="font-display text-lg leading-tight mb-2 group-hover:text-turquoise transition-colors">{e.name}</h3>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Calendar size={12} /> {e.start_date}</span>
                <span className="flex items-center gap-1"><MapPin size={12} /> {e.distance_miles} mi</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

